/**
 * `@tento/lona/sheets/platform` — platform-implementor barrel.
 *
 * Audience: anyone writing a *new* render front-end (CLI, TUI, native,
 * future canvas backend). The everyday SDK consumer (application code and
 * its tests) imports from `@tento/lona/sheets` (data model) or
 * `@tento/lona` (clients + view model). This barrel is the ONLY home of the
 * surface that only a renderer implementor cares about: per-type adapters,
 * plugin binding resolvers, scene + diff, the tree pipeline, data providers,
 * the layout engine and render plan, the render-skip ledger and the TUI
 * reference impl.
 *
 * No new logic lives here — every name is a re-export from the relevant
 * source module. A name is exported from this barrel or from
 * `@tento/lona/sheets`, never both: the data model a renderer reads (`Timescale`, the layout
 * shape types, the ephemeral-sheet serde and catalog) is imported from
 * `@tento/lona/sheets` alongside this barrel.
 *
 * @module
 */

// ─── Per-type plugin contract ────────────────────────────────────
// The role-grouped manifests + every named `*_SPEC` constant, listed by
// name (a spec added to the manifest module is not published until it is
// listed here). Platform implementors mapping over a manifest pull the
// arrays; per-type extension classes pull the named spec they extend.
export {
  ALIAS_PER_TYPE_SPECS,
  BLOCK_INERT_SPEC,
  CALENDAR_ALIAS_SPEC,
  CALENDAR_ALLDAY_ALIAS_SPEC,
  CALENDAR_LIST_ALIAS_SPEC,
  CANVAS_BAR_SPEC,
  CANVAS_CANDLESTICK_SPEC,
  CANVAS_FUNNEL_SPEC,
  CANVAS_HRULE_SPEC,
  CANVAS_LAYOUT_SPEC,
  CANVAS_LINE_SPEC,
  CANVAS_POINTS_SPEC,
  CANVAS_REGRESSION_BAND_SPEC,
  CANVAS_REGRESSION_CURVE_SPEC,
  CANVAS_REGRESSION_FILL_SPEC,
  CHART_ALIAS_SPEC,
  COLUMN_CHECKMARK_SPEC,
  COLUMN_DATA_JSON_SPEC,
  COLUMN_EVENT_LIST_SPEC,
  COLUMN_GRAPH_SPEC,
  COLUMN_HEADER_SECONDARY_SPEC,
  COLUMN_HEATMAP_SPEC,
  COLUMN_LAYOUT_SPEC,
  COLUMN_LOGS_SPEC,
  COLUMN_NUMBER_SPEC,
  COLUMN_SERIES_SUMMARY_SPEC,
  COLUMN_STICKER_SPEC,
  COLUMN_TASK_SPEC,
  COLUMN_TEXT_SPEC,
  COLUMN_TIMEZONE_SPEC,
  COLUMN_VIDEO_SPEC,
  COLUMN_WEATHER_SPEC,
  DATA_ALIAS_SPEC,
  GRID_EVENT_SPEC,
  GRID_LAYOUT_SPEC,
  GRID_TASK_SPEC,
  GROUP_INERT_SPEC,
  HEADER_INERT_SPEC,
  INERT_PER_TYPE_SPECS,
  LABEL_INERT_SPEC,
  LAYOUT_PER_TYPE_SPECS,
  PER_TYPE_SPECS,
  RENDERER_PER_TYPE_SPECS,
  SHEET_FOOTER_INERT_SPEC,
  STICKERS_ALIAS_SPEC,
  SURFACE_INERT_SPEC,
  CALENDAR_BODY_LAYOUT_SPEC,
  TASK_ALIAS_SPEC,
  TASK_LIST_ALIAS_SPEC,
  TIMELINE_COLUMN_EVENT_SPEC,
  TIMELINE_COLUMN_LAYOUT_SPEC,
  TIMELINE_COLUMN_POINTS_SPEC,
  TIMELINE_COLUMN_REGRESSION_BAND_SPEC,
  TIMELINE_COLUMN_REGRESSION_CURVE_SPEC,
  TIMELINE_COLUMN_TASK_SPEC,
  TIMELINE_COLUMN_TIMELINE_LEGEND_SPEC,
  TIMELINE_LEGEND_INERT_SPEC,
  TIMELINE_ROW_EVENT_SPEC,
  TIMELINE_ROW_LAYOUT_SPEC,
  TIMELINE_ROW_TASK_SPEC,
  TIMEZONE_ALIAS_SPEC,
  WEATHER_ALIAS_SPEC,
} from "./plugins/per-type-specs.ts";
export type {
  PerTypeAdapter,
  PerTypeSpec,
} from "./plugins/per-type-specs.ts";

// ─── Plugin catalog + binding ────────────────────────────────────
export { BUILTIN_RENDER_PLUGIN_SPECS, ROWS_ONLY_RENDERER_IDS } from "./render/render-plugin-catalog.ts";
export {
  CALENDAR_EVENT_RENDERER_PLUGIN_IDS,
  CANVAS_RENDERER_PLUGIN_IDS,
  RenderBinding,
  RenderPluginRegistry,
  RenderPluginSpec,
  rendererMarkKindOf,
  TASK_RENDERER_PLUGIN_IDS,
} from "./render/render-plugin-core.ts";
export type {
  CalendarEventRendererPluginId,
  CanvasRendererPluginId,
  RenderBindingKind,
  RenderBindings,
  RenderBindingSurface,
  RenderPluginLookup,
  RenderPluginRef,
  ShapeGateDecision,
  ShapeMismatchPolicy,
  TaskRendererPluginId,
} from "./render/render-plugin-core.ts";

// ─── Layout math (pure, non-DOM) ─────────────────────────────────
// The layout-shape vocabulary (`CellLayout`, `LayoutSpec`, …) and
// `Timescale` are data model: `@tento/lona/sheets`.
export { LayoutEngine } from "./render/layout-engine.ts";
export { RenderPlan } from "./render/render-plan.ts";
export type { RenderPass } from "./render/render-plan.ts";
export {
  defaultSubcolumnCache,
} from "./render/render-plan-subcolumns.ts";
export type { CalendarSubcolumnConfig } from "./render/render-plan-subcolumns.ts";
export {
  DEFAULT_LAYOUT_SHAPE,
  resolveLayoutShape,
} from "./render/layout-shape-resolver.ts";
export { RowRenderMode } from "./render/timescale.ts";

// S2p pre-paint geometry artifact — offsets / sizes / scroll-window geometry
// carved out of content (engine-spec §2 stage S2p): deep-frozen, byte-stable
// serde, structural reference reuse across frames.
export { GeometrySnapshot } from "./render/geometry-snapshot.ts";
export { GeometrySnapshotSerde } from "./render/geometry-snapshot-serde.ts";

// ─── The formula engine ──────────────────────────────────────────
// The engine a front-end drives formula rows with. The writers behind the
// render-pipeline switches (`RenderFlagControls`) are an app host's, declared
// on `@tento/lona/host`.
export { FormulaEngine } from "./formula/formula-engine.ts";

// ─── Render-skip ledger ──────────────────────────────────────────
// `RenderCursors` (per-sheet registry) hands out one `RenderCursor` per paint
// surface, borrowed through `acquire` → `RenderSurfaceLease`; the cursor
// composes the pure `RenderSkip` decision core.
export { RenderSkip } from "./render/ledger-render-skip.ts";
export { RenderCursors } from "./render/render-cursor.ts";
export type { RenderCursor, RenderSurfaceLease } from "./render/render-cursor.ts";

// ─── Scene + diff ────────────────────────────────────────────────
export { SceneKey } from "./render/scene.ts";
export type {
  CellContent,
  CellNode,
  LegendNode,
  RenderScene,
  RowOverlayNode,
  SceneBuildCounters,
  SceneNode,
  SceneNodeStamp,
  SceneRevision,
} from "./render/scene.ts";
export { Scene } from "./render/scene-build.ts";
export type { SceneBuildInputs, SceneRowInput } from "./render/scene-build.ts";
export { diffScene } from "./render/scene-diff.ts";
export type { ChangeKind, SceneChange, SceneDiff, SceneDiffCounters } from "./render/scene-diff.ts";
export { SceneSnapshot } from "./render/scene-serde.ts";

// ─── Tree pipeline ───────────────────────────────────────────────
export { HydratedTree, SceneTree } from "./render/build-tree.ts";
export type { BuildSceneTreeOptions } from "./render/build-tree.ts";
export { TreeWatcher } from "./sheet/tree-watcher.ts";
export type { TreeWatcherCallback } from "./sheet/tree-watcher.ts";
export { StructuralCoHide } from "./render/structural-cohide.ts";
export {
  ParsedRendered,
  ParsedTree,
  RenderedTree,
  TreeParseError,
} from "./raw/parse-tree.ts";
export type {
  SerializeRenderedTreeWithColsOptions,
  TreeChange,
  TreeChangeKind,
  TreeColumn,
  TreeDiff,
  TreeMetadata,
  TreePath,
} from "./raw/parse-tree.ts";
export {
  flatten,
  flattenSurfaces,
  isSubsumedUnderLayout,
  isSurfaceRow,
  TreeFlattener,
} from "./render/tree-flatten.ts";
export type {
  FlattenResult,
  FlattenSurfacesResult,
  TreeFlattenDeps,
  TreeFlattenStep,
} from "./render/tree-flatten.ts";

// ─── Plugin data providers (pure event/task extraction) ─────────
export type {
  CalendarSubcolumnDataProvider,
  FixedChildIntent,
  PluginDataProviders,
  TimelineItemProvider,
} from "./providers/plugin-providers.ts";
export {
  CalendarAllDayEventsProvider,
  calendarAllDayDataProvider,
} from "./providers/calendar-allday-events.ts";
export {
  CalendarTimedEventsProvider,
  calendarTimedEventsDataProvider,
} from "./providers/calendar-timed-events.ts";
export {
  CalendarListEventsProvider,
  calendarListDataProvider,
} from "./providers/calendar-list-events.ts";
export type { EventsCacheSlots, EventWithSource } from "./providers/calendar-list-events.ts";
export {
  TasksDataProvider,
  tasksDataProvider,
} from "./providers/tasks-events.ts";

// ─── The full sheet ──────────────────────────────────────────────
// What the faces (`SheetModel`, `SheetModelReads`, `@tento/lona/sheets`)
// forward to. The type is not constructed: a sheet is built from its wire
// shape by `hydrateSheet`, the implementor's builder — a renderer or CLI
// front-end that drives a standalone sheet itself (adds rows, switches its
// timescale, feeds it to the row parser and renderer instantiation below).
// An application hydrates through `RawSheet.hydrate` (`@tento/lona/sheets`),
// which hands out the same build as its reads face only; every other door
// hands a sheet out as its faces, and its SDK wiring sits on the friend doors.
export type { Sheet } from "./sheet/sheet.ts";
// The interface `Sheet` implements, named by published signatures (operator ruling 2026-10-03).
export type { ModelSheet } from "./sheet/rows-sheet.ts";
export { hydrateSheet } from "./raw/raw-sheet.ts";

// ─── Concrete row variants ───────────────────────────────────────
// Every variant subclass a renderer dispatches on — the role bases and the
// variants application code names (types on `@tento/lona/sheets`, narrowed
// there with `RowKind.of`) as the classes, and every other concrete
// variant — the default-renderer dispatch and the row parser.
export {
  AliasRow,
  CalendarAliasRow,
  ColumnNumberRow,
  FormulaDataSource,
  LayoutRow,
  RendererRow,
  SourceRow,
  ColumnTimezoneRow,
  ColumnWeatherRow,
} from "./rows/variants/index.ts";
export {
  BlockRow,
  CalendarBodyRow,
  calendarSystemCellData,
  CalendarSystemRow,
  CanvasBarRow,
  CanvasHRuleRow,
  CanvasLineRow,
  CanvasPointsRow,
  CanvasRegressionBandRow,
  CanvasRegressionCurveRow,
  CanvasRegressionFillRow,
  CanvasRow,
  ColumnCheckmarkRow,
  ColumnGraphRow,
  ColumnHeatmapRow,
  ColumnRow,
  ColumnSeriesSummaryRow,
  ColumnTextRow,
  ComputedDataSource,
  DataSource,
  GridEventRow,
  GridRow,
  GridTextRow,
  GroupRow,
  HeaderRow,
  hydrateDefaultRenderer,
  InertRow,
  instantiateRenderer,
  isSyntheticDefaultRendererId,
  ListDataSource,
  ColumnLogsRow,
  normalizeCalendarSystemName,
  parseRow,
  resolveDefaultRendererId,
  selectLayout,
  TimelineColumnRow,
  TimelineEventRow,
  TimelineNumberRow,
  ColumnTimelineRow,
  TimelineRowRow,
  TimelineSpanRow,
  TimelineTextRow,
} from "./rows/variants/index.ts";
export type { SwitchCase, SwitchConfig, SwitchWhen } from "./rows/variants/index.ts";

// ─── Transport backends ──────────────────────────────────────────
// The HTTP transport, the canonical-cell cache in front of it, and the
// encoding-normalizing layer (the in-memory `MemoryRowsBackend` is data model).
export { RowsHttpBackend, RowsHttpBackendRequestError } from "./backends/rows-http-backend.ts";
export { CanonicalBackend, isCanonicalKey } from "./backends/canonical-backend.ts";
export type { CanonicalLookup } from "./backends/canonical-backend.ts";
export { NormalizingBackend } from "./backends/normalizing-backend.ts";
export type { EncodingLookup } from "./backends/normalizing-backend.ts";

// ─── Shape / encoding / facade registries + built-in decoders ────
export { ShapeRegistry } from "./core/shape-registry.ts";
export { EncodingRegistry } from "./core/encoding-registry.ts";
export type { EncodingDecoder } from "./core/encoding-registry.ts";
export { FacadeRegistry } from "./facades/facade.ts";
export type { FacadeFactory } from "./facades/facade.ts";
export {
  EVENT_V1,
  eventV1,
  LOGS_V1,
  logsV1,
  registerBuiltinEncodings,
  SLOT_V1,
  slotV1,
  TASK_V1,
  taskV1,
  TIMEZONE_V1,
  timezoneV1,
  WEATHER_V1,
  weatherV1,
} from "./encodings/builtin.ts";

// The model layer's one-shot cache-or-fetch range read in front of a
// `Backend` (`onLoading → onData → onDataFull`, a failed fetch reads `[]`);
// not `@tento/lona`'s `RowRangeCellHandle`, which follows the row's sync and
// rejects a failed read.

// ─── Local-only cell seeding ─────────────────────────────────────
// For an offline / ephemeral sheet a front-end renders (nothing persists;
// the ordinary cell-change events fire). The ephemeral-sheet serde and the
// reserved-sheet catalog are data model: `@tento/lona/sheets`.
export { seedCells } from "./rows/seed-cells.ts";

// ─── Moved off `@tento/lona/sheets` ──────────────────────────────
// Engine and implementor names the main barrel carried with no application
// consumer: the ingestion gate, the sheet registry and sync-key watcher, the
// built-in alias templates, data-model details a renderer reads.
export { ALIAS_TEMPLATES } from "./aliases/alias-templates.ts";
export { BodyViewTree } from "./render/body-view-tree.ts";
export { calendarConfigCell } from "./providers/calendar-config-provider.ts";
export { type RegressionData } from "@tento-core/chart/graph-data";
export { Attendee, type OpaqueJson } from "./core/dtype.ts";
export { LIST_AGGREGATE_FACADE, ListAggregateFacade } from "./facades/index.ts";
export {
  type DataPresetSpec,
  type EventRef,
  EventTimeRange,
  eventValues,
  type GroupedCellAdapter,
  RecurrenceInfo,
  recurringMasterDtstartExcluded,
} from "./indices/index.ts";
export { IngestionGate } from "./sync/ingestion-gate.ts";
export { InstallPacer } from "./core/install-pacer.ts";
export {
  CALENDAR_PREFERENCE_KINDS,
  type CalendarPreferenceKind,
  pathMatches,
} from "./tx/invalidation.ts";
export { type RowLayoutHeights } from "./render/layout-types.ts";
export { type RegressionPoint } from "./regression/kernel-smooth.ts";
export { isRendererWireType } from "./render/renderer-kind.ts";
export { DATA_GRAPH_ROW_HEIGHTS } from "./rows/row-type-spec.ts";
export { SheetViewConfigRaw } from "./sheet/sheet-view-config.ts";
export { type RowMetadata } from "./stores/row-metadata-store.ts";
export { type BaseAttributes } from "./sync/sync-state.ts";
export { SyncKeyWatcher } from "./sync/sync-watcher.ts";
export { addTreeChildRows, SURFACES_STATIC } from "./rows/system-rows.ts";
export { type RendererAttrs } from "./rows/variants/index.ts";

// ─── Implementor names moved off the main barrel ─────────────────
// Read by the SDK's own front-ends through their friend entries
// (`@tento-lona/sheets/internal-sdk` for `@tento/lona`,
// `@tento-lona/sheets/internal-ui` for sheets-ui and the CLI). These stay
// here because a published example, a published doc page or the node
// registry reads them: the facade keys and their facade types, the data
// series reads and the chart cast, the row type-spec operations, a raw row's
// wire type, the builtin catalog, the cell-cardinality vocabulary.
export type { CellCardinality } from "./core/dtype.ts";
export {
  EVENTS_FACADE,
  EventsFacade,
  REGRESSION_FACADE,
  RegressionFacade,
  SCHEDULING_LINK_FACADE,
  TASKS_FACADE,
  TasksFacade,
  WEATHER_FACADE,
} from "./facades/index.ts";
export { EVENT_SHAPE, extractSeries } from "./indices/index.ts";
export type { SeriesResult } from "./indices/index.ts";
export { wireTypeOf } from "./raw/raw-sheet.ts";
export { RowTypeSpecOps } from "./rows/row-type-spec.ts";
export { ephemeralFromCatalog } from "./raw/ephemeral-sheet-catalog.ts";
export { castData } from "@tento-core/chart/chart-types";
export { SystemRowPosition } from "./rows/system-rows.ts";

// The types the published signatures name (tools/lint/published-type-refs).
export type { CanvasConfig, RetiredRendererType } from "./rows/descriptor.ts";
export type { ColumnKey } from "./render/layout-types.ts";
export type { SurfaceProjection } from "./render/projection.ts";
export type { RenderPluginAcceptedShape } from "./render/render-plugin-core.ts";
export type { RowSection } from "./render/scene.ts";
export type { RowCtor } from "./sheet/sheet.ts";
export type { SyncWatchOutcome } from "./sync/sync-watcher.ts";

// Complete geometry residency shared by DOM and terminal renderers.
